import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'components/UI/Modal';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Border, Main, Text } from 'styles/themeColors';
import { FontWeight } from 'lib/styles';
import { TEXT_LABELS } from 'constants/order';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import { onlyWeekdaysAllowed } from 'utils/product';
import WarningBanner from 'components/UI/WarningBanner';
import OrderAddonTablePreview from 'common/OrderAddonTablePreview';
import { ProductAddonPreview } from 'data/Service';

const LabelText = styled.span<any>`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  margin-bottom: 5px;
  font-size: 15px;
  color: ${({ color }) => color};
`;

const LabelTextError = styled.span<any>`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  margin-bottom: 5px;
  font-size: 12px;
  color: ${Main.error2};
`;

const TextArea = styled.textarea<any>`
  border: 1px solid ${({ error }) => error ? `${Main.error2}` : `${Border.main}`};
  border-radius: 6px;
  padding: 7px;
  color: ${({ color }) => color};
`;



export interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: (value: string) => void;
  title: string;
  disabled?: boolean;
  rentalStartDate: string;
  rentalEndDate: string;
  reason: string;
  setRentalStartDate: (date: string) => void;
  setRentalEndDate: (date: string) => void;
  setReason: (reason: string) => void;
  weekdaysOnly: boolean;
  consignmentEntries?: ProductAddonPreview[][],
  onAddEntry?: (val: ProductAddonPreview) => void;
  onEditEntry?: (id: number, value: any) => void;
  onDeleteEntry?: (id: number) => void;
  vendorId?: number;
  country?: string;
  dateError?: boolean;
  reasonError?: boolean;
  setDateError?: (val: boolean) => void;
  setReasonError?: (val: boolean) => void;
  setIsAddonTableModified?: (val: boolean) => void;
  consignmentId?: string;
};

const ModifyRentalDatesModal = ({
  show,
  onClose,
  onConfirm,
  title,
  disabled = false,
  rentalEndDate,
  rentalStartDate,
  setRentalStartDate,
  setRentalEndDate,
  reason,
  setReason,
  weekdaysOnly,
  consignmentEntries = [],
  onAddEntry,
  onEditEntry,
  onDeleteEntry,
  vendorId,
  country,
  dateError,
  reasonError,
  setDateError,
  setReasonError,
  consignmentId
}: Props) => {
  const [openDatePicker, setOpenDatePicker] = useState<'start' | 'end' | null>(null);

  return (
    <Modal
      showButtons
      showModal={show}
      title={title}
      onClose={onClose}
      onClick={onConfirm}
      disabled={disabled}
      ghostButtonText={'Cancel'}
      primaryButtonText={'Modify'}
      width='1057px'
      left='20%'
      primaryButtonColor={Main.brandDark}
      color={Main.darkGray}
      borderColor={Main.lightGray}
      className='rent-modification-max-height'
    >
      <FlexboxContainer flexDirection="column">
        <FlexboxContainer flexDirection='row' gap='4px'>
          <FlexboxContainer flexDirection="column" width='100%'>
            <LabelText color={dateError ? Main.error2 : Text.primary}>{TEXT_LABELS.RENTAL_START_DATE}</LabelText>
            <DatePicker
              value={rentalStartDate ? DateTime.fromISO(rentalStartDate) : null}
              format="yyyy MMM dd"
              open={openDatePicker === 'start'}
              onOpen={() => setOpenDatePicker('start')}
              onClose={() => setOpenDatePicker(null)}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  error: dateError,
                  onClick: () => setOpenDatePicker('start'),
                  InputProps: {
                    readOnly: true,
                    sx: { cursor: 'pointer' }
                  }
                }
              }}
              onChange={(date: DateTime | null) => { setDateError?.(false); setRentalStartDate(date?.toISO() || '') }}
              shouldDisableDate={(date: DateTime) => onlyWeekdaysAllowed(date, weekdaysOnly)}
            />
            {
              dateError &&
              <LabelTextError>{TEXT_LABELS.START_DATE_INVALID}</LabelTextError>
            }
          </FlexboxContainer>
          <FlexboxContainer flexDirection="column" width='100%'>
            <LabelText color={dateError ? Main.error2 : Text.primary}>{TEXT_LABELS.RENTAL_END_DATE}</LabelText>
            <DatePicker
              value={rentalEndDate ? DateTime.fromISO(rentalEndDate) : null}
              format="yyyy MMM dd"
              open={openDatePicker === 'end'}
              onOpen={() => setOpenDatePicker('end')}
              onClose={() => setOpenDatePicker(null)}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  error: dateError,
                  onClick: () => setOpenDatePicker('end'),
                  InputProps: {
                    readOnly: true,
                    sx: { cursor: 'pointer' }
                  }
                }
              }}
              onChange={(date: DateTime | null) => { setDateError?.(false); setRentalEndDate(date?.toISO() || '') }}
              shouldDisableDate={(date: DateTime) => onlyWeekdaysAllowed(date, weekdaysOnly)}
            />
          </FlexboxContainer>
        </FlexboxContainer>
        <FlexboxContainer flexDirection="column" margin='10px 0px 0px 0px'>
          <LabelText color={reasonError ? Main.error2 : Text.primary}>{TEXT_LABELS.RENTAL_MODIFICATOION_REASON}</LabelText>
          <TextArea
            maxLength={500}
            rows={5}
            value={reason}
            onChange={(event: any) => { setReasonError?.(false); setReason(event.target.value) }}
            error={reasonError}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
              }
            }}
          />
          {
            reasonError &&
            <LabelTextError>{TEXT_LABELS.BLANK_FIELD}</LabelTextError>
          }
        </FlexboxContainer>

        <WarningBanner message={TEXT_LABELS.RENT_MODIFICATION_WARNING} margin='20px 0px' />

        {
          consignmentEntries.map((entry, index) => (
            <OrderAddonTablePreview
              key={index}
              country={country}
              products={entry}
              title=''
              onAddEntry={onAddEntry!}
              onEditEntry={onEditEntry!}
              onDeleteEntry={onDeleteEntry!}
              vendorId={vendorId!}
              backgroundColor="#fff"
              consignmentId={consignmentId!}
            />
          ))
        }

      </FlexboxContainer>
    </Modal>
  );
};

export default ModifyRentalDatesModal;
